import * as React from 'react';
import { observer } from 'mobx-react';
import { Api } from '@api/index';
import {
    ConnectApiStudioAddressDto,
    CentralLegalLinkDto
} from '../../core/api/dtos';
import { injectTSDI } from '../../core/tsdi';
import { I18nStore } from '../../core/i18n-store';

import { openAside } from '../aside';
import './footer.scss';
import { CookieNoticeStore } from '../cookie-notice/store';
import { cancelUrl } from '../../app/utils';
import { findDisplayValue } from '../../core/multi-language';

interface FooterProps {
    legalLinks: CentralLegalLinkDto[];
    studioAddress?: ConnectApiStudioAddressDto;
    studioId?: number;
    germanStudio?: boolean;
    fixed?: boolean;
}

export const Footer = observer((props: FooterProps) => {
    const { legalLinks, studioAddress, germanStudio, fixed } = props;
    const { translate } = injectTSDI(I18nStore);
    const { openCookieNotice } = injectTSDI(CookieNoticeStore);
    const { tenantName } = injectTSDI(Api);

    return (
        <footer className={classnames({ 'fixed-footer': fixed })}>
            <div className="footer">
                {studioAddress && (
                    <div className="footer__studio-address">
                        <p>
                            {studioAddress.street} {studioAddress.houseNumber},{' '}
                            {studioAddress.zipCode} {studioAddress.city}
                        </p>
                        <a
                            target="_blank"
                            href={`https://maps.google.com/?q=${studioAddress.latitude},${studioAddress.longitude}`}
                            rel="noreferrer"
                        >
                            <button>
                                {translate('onlinepages.show.on.map')}
                            </button>
                        </a>
                    </div>
                )}
                <ul className="footer__imprint-nav">
                    {legalLinks.map((link) => (
                        <LegalLink key={link.databaseId} link={link} />
                    ))}
                    {tenantName && germanStudio && (
                        <li>
                            <a
                                href={cancelUrl(tenantName, props.studioId)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <span>
                                    {translate(
                                        'onlinepages.cancel.contract.url'
                                    )}
                                </span>
                            </a>
                        </li>
                    )}
                    <li onClick={openCookieNotice}>
                        <span>
                            {translate(
                                'online.pages.configuration.cookie.settings'
                            )}
                        </span>
                    </li>
                </ul>
            </div>
        </footer>
    );
});

interface LegalLinkProps {
    link: CentralLegalLinkDto;
}

const LegalLink = observer(({ link }: LegalLinkProps) => {
    if (!link.centralLegalLinkDocumentType) {
        return null;
    }

    const title = centralLegalLinkDocumentTypeText(
        link.centralLegalLinkDocumentType
    );

    if (link.legalLinkType === 'EXTERNAL_URL') {
        return (
            <li>
                <a
                    href={findDisplayValue(link.contents)}
                    target="_blank"
                    rel="noreferrer"
                >
                    <span>{title}</span>
                </a>
            </li>
        );
    }

    if (link.legalLinkType === 'TEXT') {
        return (
            <li
                onClick={() =>
                    openAside(title, findDisplayValue(link.contents))
                }
            >
                <span>{title}</span>
            </li>
        );
    }

    return null;
});

const centralLegalLinkDocumentTypeText = (
    type: NonNullable<CentralLegalLinkDto['centralLegalLinkDocumentType']>
) => {
    const { translate } = injectTSDI(I18nStore);
    switch (type) {
        case 'IMPRINT':
            return translate(
                'configuration.legalorganizationunit.legal.links.imprint'
            );
        case 'PRIVACY':
            return translate(
                'configuration.legalorganizationunit.legal.links.privacy'
            );
        case 'TERMS_AND_CONDITIONS':
            return translate(
                'configuration.legalorganizationunit.legal.links.terms'
            );
        case 'REVOCATION':
            return translate(
                'configuration.legalorganizationunit.legal.links.revocation'
            );
    }
};
